import { Flag } from 'lucide-react';
import { useRef } from 'react';
import { useSearchParams } from 'react-router';

import { PermissionGate } from '@/auth/PermissionGate';
import { Barras, Dona, Linea } from '@/components/Graficas';
import { AVATARES } from '@/lib/avatares';
import { useRevealProgress, useStagger } from '@/lib/motion';
import { Leyenda, TarjetaRotulo } from '@/components/BloquesConsalud';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga, TabsModulo } from '../curso/componentes';

import { colorCalificacion } from './comun';
import { useAvances, type ProgressSummary } from './queries';

type Bloque = ProgressSummary['simulator'];
const PESTANAS = [
  { key: 'curso', label: 'Curso' },
  { key: 'simulador', label: 'Simulador' },
  { key: 'examenes', label: 'Exámenes' },
] as const;
type Pestana = (typeof PESTANAS)[number]['key'];

/** Avances (port de `inicio/avances/*` y `curso-enarm/avances`; sin QuizENARM, decisión D17). */
export function AvancesPage({ inicial, modulo }: { inicial: Pestana; modulo?: boolean }) {
  return (
    <PermissionGate permiso="progress.access" modulo="Avances">
      <Avances inicial={inicial} {...(modulo ? { modulo } : {})} />
    </PermissionGate>
  );
}

function Avances({ inicial, modulo }: { inicial: Pestana; modulo?: boolean }) {
  const { data, error, isPending } = useAvances();
  const [params, setParams] = useSearchParams();
  const pestana = PESTANAS.find((p) => p.key === params.get('vista'))?.key ?? inicial;
  const root = useRef<HTMLDivElement>(null);
  useStagger(root, '[data-card]', { step: 70, deps: [pestana, data] });

  return (
    <div ref={root} className="space-y-6">
      {modulo && <TabsModulo />}
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Avances</span>
      </h1>
      <div className="flex flex-wrap gap-2">
        {PESTANAS.map((p) => (
          <button
            key={p.key}
            type="button"
            className="btn-modulo"
            data-state={pestana === p.key ? 'active' : undefined}
            onClick={() => setParams({ vista: p.key }, { replace: true })}
          >
            {p.label}
          </button>
        ))}
      </div>
      {isPending && <Cargando alto="h-96" />}
      {error && <ErrorCarga error={error} />}
      {data && pestana === 'curso' && <Curso d={data} />}
      {data && pestana === 'simulador' && (
        <Seccion
          titulo="Resultados de simuladores"
          conteo="Total de simuladores"
          b={data.simulator}
          linea="Simuladores por semana"
        />
      )}
      {data && pestana === 'examenes' && (
        <div className="space-y-14">
          <Seccion
            titulo="Resultados de exámenes"
            conteo="Total de exámenes"
            b={data.exams}
            linea="Exámenes por semana"
          />
          <Seccion
            titulo="Resultados de simulacros"
            conteo="Total de simulacros"
            b={data.drills}
            linea="Simulacros por semana"
          />
        </div>
      )}
    </div>
  );
}

function TablaAreas({ b }: { b: Bloque }) {
  return (
    <table className="tabla-cs">
      <thead>
        <tr>
          <th>Especialidad</th>
          <th>%</th>
          <th>Correctas</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {b.areas.map((a) => (
          <tr key={a.key}>
            <td>{a.name}</td>
            <td
              className={cn(
                'font-extrabold tabular',
                a.total ? colorCalificacion(a.percent) : 'text-texto-3',
              )}
            >
              {a.total ? `${String(a.percent)}%` : '—'}
            </td>
            <td className="tabular">{a.correct}</td>
            <td className="tabular">{a.total}</td>
          </tr>
        ))}
        <tr data-destacada="">
          <td className="font-extrabold">Total</td>
          <td className="font-extrabold tabular">
            {b.total.total ? `${String(b.total.percent)}%` : '—'}
          </td>
          <td className="tabular">{b.total.correct}</td>
          <td className="tabular">{b.total.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

function Seccion({
  titulo,
  conteo,
  b,
  linea,
}: {
  titulo: string;
  conteo: string;
  b: Bloque;
  linea: string;
}) {
  // Área con menor rendimiento (solo las que tienen preguntas), como el texto del legacy.
  const debil = b.areas
    .filter((a) => a.total > 0)
    .reduce<Bloque['areas'][number] | null>((m, a) => (!m || a.percent < m.percent ? a : m), null);
  return (
    <div className="grid gap-x-6 gap-y-10 pt-4 lg:grid-cols-3">
      <TarjetaRotulo rotulo={titulo} color="azul">
        <p className="text-sm text-azul-texto">
          {conteo}: <strong className="text-texto tabular">{b.count}</strong>
        </p>
        <ResumenDona b={b} />
      </TarjetaRotulo>
      <div className="space-y-10 lg:col-span-2">
        <TarjetaRotulo rotulo="Estadísticas por especialidad" color="verde">
          <div className="grid items-center gap-6 md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <div className="space-y-3 text-center">
              <Persistencia />
              {debil && (
                <p className="text-sm text-texto-2">
                  Te recomendamos repasar más el área de{' '}
                  <strong className="text-texto">{debil.name}</strong> porque es donde has
                  presentado menor rendimiento.
                </p>
              )}
            </div>
            <Barras items={b.areas.map((a) => ({ key: a.key, label: a.name, value: a.percent }))} />
          </div>
        </TarjetaRotulo>
        <section data-card className="panel-vidrio">
          <Linea semanas={b.weekly} etiqueta={linea} />
        </section>
      </div>
    </div>
  );
}

/** Dona + leyenda Correctas/Incorrectas + «Aciertos por especialidad» (tarjeta del legacy). */
function ResumenDona({ b }: { b: Bloque }) {
  return (
    <div className="text-center">
      <div className="mt-3 grid place-items-center">
        <Dona percent={b.total.percent} etiqueta="Correctas" />
      </div>
      <div className="mt-3 flex justify-center gap-8 text-sm">
        <Leyenda color="#1CD0C7" etiqueta="Correctas" valor={`${String(b.total.percent)}%`} />
        <Leyenda
          color="#BADCE5"
          etiqueta="Incorrectas"
          valor={b.total.total ? `${String(100 - b.total.percent)}%` : '0%'}
        />
      </div>
      <hr className="my-4 border-t border-dashed border-texto-3/40" />
      <h3 className="text-sm font-extrabold uppercase">Aciertos por especialidad</h3>
      <div className="mt-2 overflow-x-auto text-left">
        <TablaAreas b={b} />
      </div>
    </div>
  );
}

function Persistencia() {
  return (
    <div className="text-verde-texto">
      <p>
        <em>«La persistencia es la clave del éxito»</em>
      </p>
      <p className="font-extrabold">Sigue estudiando diariamente</p>
    </div>
  );
}

function Curso({ d }: { d: ProgressSummary }) {
  const c = d.course;
  const barra = useRef<HTMLDivElement>(null);
  useRevealProgress(barra, c.classes.percent);
  const recomendacion =
    c.weakest.length === 0
      ? 'Te recomendamos repasar diariamente y estudiar todas las áreas.'
      : `Te recomendamos repasar más ${c.weakest.length === 1 ? 'el área' : 'las áreas'} de ${c.weakest.join(', ').replace(/, ([^,]*)$/, ' y $1')}, donde has tenido menor rendimiento.`;
  return (
    <div className="grid gap-x-6 gap-y-10 pt-4 lg:grid-cols-3">
      <TarjetaRotulo rotulo="Resultados de exámenes" color="azul">
        <ResumenDona b={c} />
      </TarjetaRotulo>
      <TarjetaRotulo rotulo="Estadísticas por especialidad" color="verde" className="space-y-4">
        <div className="text-center">
          <Persistencia />
        </div>
        <p className="text-center text-sm text-texto-2">{recomendacion}</p>
        <Barras items={c.areas.map((a) => ({ key: a.key, label: a.name, value: a.percent }))} />
        <p className="text-center text-xs font-extrabold text-texto-3">% ESPECIALIDADES</p>
      </TarjetaRotulo>
      <div data-card className="space-y-6">
        {d.enarm?.daysLeft != null && d.enarm.daysLeft >= 0 && (
          <div className="panel-vidrio relative border-l-4 border-l-amarillo-400 text-center">
            <img
              src={AVATARES.doctorSenala}
              alt=""
              className="pointer-events-none absolute -top-10 -right-4 h-24 w-auto"
            />
            <p>
              No te confíes, <strong>¡esfuérzate más!</strong>
            </p>
            <p className="text-sm text-texto-2">Recuerda que tu examen se acerca y solo tienes…</p>
            <p className="text-3xl font-extrabold text-azul-texto tabular">
              {d.enarm.daysLeft} días
            </p>
            <p className="text-texto-2">
              para el ENARM <strong className="text-texto">{d.enarm.year}</strong>
            </p>
          </div>
        )}
        <div className="panel-vidrio space-y-3">
          <h2 className="text-center font-extrabold text-azul-texto uppercase">
            Progreso en clases
          </h2>
          <div className="flex justify-between gap-2 text-sm text-texto-2">
            <p>
              Total de clases: <strong className="text-texto tabular">{c.classes.total}</strong>
            </p>
            <p>
              Clases vistas: <strong className="text-texto tabular">{c.classes.seen}</strong>
            </p>
          </div>
          <div className="relative h-3 overflow-hidden rounded-full bg-[var(--pista)]">
            <div ref={barra} className="h-full w-full origin-left rounded-full bg-[#13D0C9]" />
          </div>
          <p className="flex items-center justify-end gap-1 text-xs font-bold text-azul-texto tabular">
            <Flag size={12} /> {c.classes.percent}%
          </p>
        </div>
      </div>
    </div>
  );
}
